import { useEffect, useMemo, useRef, useState } from 'react';
import './GamePlayOverlay.css';
import { fetchGameCards, getGameAccess } from '../../api/games';
import { formatText } from '../../utils/typografUtils';
import { applyGender, getCachedGender } from '../../utils/genderUtils';
import { saveUserNote } from '../../api/notes';
import { isCardSaved, saveCard, unsaveCard, getSavedCardsByGame } from '../../api/savedCards';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import { createShareImage } from '../../utils/shareImageUtils';
import { invalidatePageCache } from '../../hooks/usePageCache';

// Глобальный кэш для thumb картинок
const thumbCache = new Map();

// Глобальный кэш для SVG иконок
const iconCache = new Map();

export default function GamePlayOverlay({ game, onClose = () => { }, savedCardsMode = false, initialShowAll = false }) {
  const [cards, setCards] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [bgUrl, setBgUrl] = useState('');
  const [prevBgUrl, setPrevBgUrl] = useState('');
  const [isNewCardAnimating, setIsNewCardAnimating] = useState(false);
  const [isBgTransitioning, setIsBgTransitioning] = useState(false);
  const [showAllCards, setShowAllCards] = useState(initialShowAll);
  // eslint-disable-next-line no-unused-vars
  const [hasViewedCard, setHasViewedCard] = useState(false);
  // Удаляем состояния ленивой загрузки - теперь используем виртуализацию
  const [showNoteWriting, setShowNoteWriting] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isNoteLoading, setIsNoteLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  // Удалены неиспользуемые refs для ленивой загрузки

  // Состояние для виртуализации
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const virtualizedContainerRef = useRef(null);

  // Состояние для отслеживания сохраненной карточки
  const [isCurrentCardSaved, setIsCurrentCardSaved] = useState(false);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialLimit, setTrialLimit] = useState(0);
  const [allCards, setAllCards] = useState([]);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  // Получаем высоту клавиатуры
  const keyboardHeight = useKeyboardHeight();

  // Отладочная информация (можно убрать в продакшене)
  useEffect(() => {
    if (showNoteWriting) {
      console.log('Keyboard height:', keyboardHeight);
    }
  }, [keyboardHeight, showNoteWriting]);

  // Константы для виртуализации
  const COLUMNS = 4; // Количество колонок
  const CARD_ASPECT_RATIO = 0.8; // Соотношение сторон (ширина/высота)
  const GAP = 8; // Отступ между карточками в px

  // Вычисляем размеры карточек на основе размера контейнера
  const getCardDimensions = () => {
    if (!containerSize.width || containerSize.width <= 0) {
      return { width: 80, height: 100 }; // значения по умолчанию
    }

    // Учитываем отступы контейнера (16px с каждой стороны)
    const availableWidth = containerSize.width - 32; // 16px * 2
    const totalGapWidth = (COLUMNS - 1) * GAP;
    const cardWidth = Math.floor((availableWidth - totalGapWidth) / COLUMNS);
    const cardHeight = Math.floor(cardWidth / CARD_ASPECT_RATIO);

    return { width: cardWidth, height: cardHeight };
  };

  // Функция для получения картинки из кэша
  const getCachedThumb = (url) => {
    return thumbCache.get(url);
  };

  // Функция для получения оптимального URL картинки
  // Temporarily simplified: always return thumb URL directly
  const getOptimalImageUrl = (card) => {
    // Return thumb URL directly, skip blob cache for now
    return card.cardThumbUrl || card.cardPicUrl;
  };

  // Функция для предзагрузки SVG иконок
  const preloadIcons = () => {
    const icons = [
      '/icons/back-arrow.svg',
      '/icons/instructions.svg',
      '/icons/random.svg',
      '/icons/cart.svg'
    ];

    icons.forEach(iconPath => {
      if (!iconCache.has(iconPath)) {
        fetch(iconPath)
          .then(response => response.text())
          .then(svgText => {
            iconCache.set(iconPath, svgText);
          })
          .catch(error => {
            console.warn(`Failed to preload icon: ${iconPath}`, error);
          });
      }
    });
  };

  // Компонент для оптимизированного отображения иконок
  const OptimizedIcon = ({ src, alt, ...props }) => {
    const cachedSvg = iconCache.get(src);

    if (cachedSvg) {
      // Если SVG в кэше, используем его напрямую
      return (
        <span
          {...props}
          dangerouslySetInnerHTML={{ __html: cachedSvg }}
          style={{ display: 'inline-block', width: '100%', height: '100%' }}
        />
      );
    }

    // Если SVG не в кэше, используем обычный img
    return <img src={src} alt={alt} {...props} />;
  };


  // Функция предзагрузки thumb картинок с кэшированием
  const preloadThumbImages = (cardsData) => {
    // Используем requestIdleCallback для предзагрузки в фоне
    const preloadInBackground = () => {
      cardsData.forEach((card) => {
        if (card.cardThumbUrl) {
          const existingCache = thumbCache.get(card.cardThumbUrl);

          // Проверяем, есть ли уже кэшированная картинка и валидна ли она
          if (existingCache && existingCache.src) {
            if (existingCache.src.startsWith('blob:')) {
              // Проверяем валидность blob URL
              const testImg = new Image();
              testImg.onerror = () => {
                // Если blob URL невалиден, удаляем из кэша и перезагружаем
                thumbCache.delete(card.cardThumbUrl);
                loadThumbImage(card.cardThumbUrl);
              };
              testImg.src = existingCache.src;
            }
            // Если это обычный Image объект, он должен быть валиден
            return;
          }

          // Если картинки нет в кэше или она невалидна, загружаем
          loadThumbImage(card.cardThumbUrl);
        }
      });
    };

    // Функция для загрузки отдельной картинки
    const loadThumbImage = (thumbUrl) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Для возможности создания blob URL
      img.onload = () => {
        // Создаем canvas для конвертации в blob URL
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            // Сохраняем blob URL в кэш
            thumbCache.set(thumbUrl, { src: blobUrl, originalImg: img });
          } else {
            // Fallback на обычную картинку
            thumbCache.set(thumbUrl, img);
          }
        }, 'image/webp', 0.8);
      };
      img.onerror = () => {
        // Помечаем как неудачную загрузку, чтобы не пытаться снова
        thumbCache.set(thumbUrl, null);
      };
      img.src = thumbUrl;
    };

    // Если браузер поддерживает requestIdleCallback, используем его
    if (window.requestIdleCallback) {
      window.requestIdleCallback(preloadInBackground, { timeout: 2000 });
    } else {
      // Fallback для старых браузеров
      setTimeout(preloadInBackground, 100);
    }
  };

  // Предзагрузка иконок при монтировании
  useEffect(() => {
    preloadIcons();
  }, []);



  // Load cards from Supabase by game id
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        if (!game?.id) {
          if (isMounted) {
            setCards([]);
          }
          return;
        }

        // Очищаем кэш при смене игры, чтобы избежать проблем с устаревшими blob URL
        thumbCache.clear();

        let finalCards = [];

        if (savedCardsMode) {
          // В режиме сохраненных карточек
          if (game.savedCards && game.savedCards.length > 0) {
            // Если карточки уже переданы (режим "Все")
            finalCards = game.savedCards;
          } else {
            // Если нужно загрузить карточки для конкретной игры
            finalCards = await getSavedCardsByGame(game.id);
          }

          console.log('Loaded saved cards:', finalCards);
        } else {
          // Обычный режим игры
          // Загружаем карты и проверяем доступ параллельно
          const [cardsData, accessData] = await Promise.all([
            fetchGameCards(game.id),
            getGameAccess(game.id)
          ]);

          console.log('Loaded cards from fetchGameCards:', cardsData);
          console.log('Game access:', accessData);

          // Если пробная версия, разделяем доступные и недоступные карты
          if (accessData?.access === 'trial') {
            setIsTrial(true);
            const limit = accessData.trial_limit || 7;
            setTrialLimit(limit);
            console.log(`Trial access detected. Limiting playable cards to ${limit}`);

            // Сохраняем все карты для отображения в меню
            setAllCards(cardsData);

            // Для игры используем только доступные карты
            finalCards = cardsData.slice(0, limit);
          } else {
            setIsTrial(false);
            setTrialLimit(0);
            setAllCards([]);
            finalCards = cardsData;
          }
        }

        if (isMounted) {
          setCards(finalCards);

          // Предзагружаем все thumb картинки (включая недоступные для меню)
          const cardsToPreload = isTrial && allCards.length > 0 ? allCards : finalCards;
          preloadThumbImages(cardsToPreload);

          // В режиме сохраненных открываем первую карточку (новые сверху)
          // В обычном режиме выбираем случайную
          if (finalCards && finalCards.length > 0) {
            if (savedCardsMode) {
              setActiveIndex(0);
            } else {
              const randomIndex = Math.floor(Math.random() * finalCards.length);
              // В trial режиме добавляем смещение +1, так как на 0-м индексе промо-карточка
              const initialIndex = isTrial ? randomIndex + 1 : randomIndex;
              setActiveIndex(initialIndex);
            }
          }
        }
      } catch (e) {
        console.error('Error loading game data:', e);
        if (isMounted) setCards([]);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id, savedCardsMode]);

  // Создаем массив карточек с промо-вставками для trial режима
  const cardsWithPromo = useMemo(() => {
    if (!isTrial || !cards.length) return cards;

    // Вставляем промо-карточку на 0-й слот
    return [{
      id: 'promo-start',
      isPromo: true,
      cardPicUrl: '/login-picture.png',
      question1: 'Полный набор',
      question2: 'Купите полную версию и откройте все карточки'
    }, ...cards];
  }, [cards, isTrial]);

  const getPrevIndex = (i) => {
    const displayCards = isTrial ? cardsWithPromo : cards;
    return displayCards.length ? (i - 1 + displayCards.length) % displayCards.length : 0;
  };

  const getNextIndex = (i) => {
    const displayCards = isTrial ? cardsWithPromo : cards;
    return displayCards.length ? (i + 1) % displayCards.length : 0;
  };

  const activeCard = useMemo(() => {
    const displayCards = isTrial ? cardsWithPromo : cards;
    return displayCards[activeIndex];
  }, [cards, cardsWithPromo, activeIndex, isTrial]);
  // eslint-disable-next-line no-unused-vars
  const prevCard = useMemo(() => {
    const displayCards = isTrial ? cardsWithPromo : cards;
    return displayCards[getPrevIndex(activeIndex)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, cardsWithPromo, activeIndex, isTrial]);
  // eslint-disable-next-line no-unused-vars
  const nextCard = useMemo(() => {
    const displayCards = isTrial ? cardsWithPromo : cards;
    return displayCards[getNextIndex(activeIndex)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, cardsWithPromo, activeIndex, isTrial]);

  const goPrev = () => {
    if (cards.length && !isNewCardAnimating) {
      setIsNewCardAnimating(true);
      setActiveIndex((i) => getPrevIndex(i));
      setTimeout(() => {
        setIsNewCardAnimating(false);
      }, 300);
    }
  };

  const goNext = () => {
    if (cards.length && !isNewCardAnimating) {
      setIsNewCardAnimating(true);
      setActiveIndex((i) => getNextIndex(i));
      setTimeout(() => {
        setIsNewCardAnimating(false);
      }, 300);
    }
  };

  const handleProgressBadgeClick = () => {
    setShowAllCards(true);
    // Принудительно обновляем размер контейнера после открытия
    setTimeout(() => {
      updateContainerSize();
    }, 100);
  };

  const handleAllCardsClose = () => {
    setShowAllCards(false);
  };

  const handleCardSelect = (index) => {
    // Проверяем, заблокирована ли карточка в trial режиме
    if (isTrial && trialLimit > 0 && index >= trialLimit) {
      setShowPurchaseDialog(true);
      return;
    }

    // В trial режиме добавляем смещение +1, так как на 0-м индексе промо-карточка
    const targetIndex = isTrial ? index + 1 : index;

    setActiveIndex(targetIndex);
    setShowAllCards(false);
    setHasViewedCard(true);
  };

  const handleOpenNoteWriting = async () => {
    setShowNoteWriting(true);
    // Всегда начинаем с пустой заметки, чтобы можно было создавать несколько заметок на одну карточку
    setNoteText('');
  };

  const handleCloseNoteWriting = () => {
    setShowNoteWriting(false);
    setNoteText('');
  };

  // Функция для подтверждения закрытия заметки с черновиком
  const confirmCloseNoteWriting = () => {
    if (noteText.trim()) {
      // Если есть текст в заметке, показываем диалог подтверждения
      setShowConfirmDialog(true);
    } else {
      // Если заметка пустая, закрываем без подтверждения
      handleCloseNoteWriting();
    }
  };

  // Функция для подтверждения удаления черновика
  const handleConfirmDelete = () => {
    setShowConfirmDialog(false);
    handleCloseNoteWriting();
  };

  // Функция для отмены удаления черновика
  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;

    console.log('handleSaveNote called');
    console.log('game.id:', game.id);
    console.log('activeCard:', activeCard);
    console.log('activeCard.id:', activeCard?.id);
    console.log('noteText:', noteText.trim());

    setIsNoteLoading(true);
    try {
      await saveUserNote(game.id, activeCard.id, noteText.trim());
      // Инвалидируем кеш заметок, чтобы новая заметка появилась в списке
      invalidatePageCache('my-notes');
      setShowNoteWriting(false);
      setNoteText('');
    } catch (error) {
      console.error('Ошибка при сохранении заметки:', error);
      alert('Не удалось сохранить заметку. Попробуйте еще раз.');
    } finally {
      setIsNoteLoading(false);
    }
  };

  const handleNoteTextChange = (e) => {
    setNoteText(e.target.value);
  };

  // Функция для переключения закладки
  const handleBookmarkToggle = async () => {
    if (!activeCard?.id || isBookmarkLoading) return;

    setIsBookmarkLoading(true);

    // Оптимистичное обновление UI
    const newSavedState = !isCurrentCardSaved;
    setIsCurrentCardSaved(newSavedState);

    try {
      let success;
      if (newSavedState) {
        success = await saveCard(activeCard.id);
      } else {
        success = await unsaveCard(activeCard.id);
      }

      if (!success) {
        // Откатываем изменения при ошибке
        setIsCurrentCardSaved(!newSavedState);
        alert('Не удалось обновить закладку. Попробуйте еще раз.');
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      // Откатываем изменения при ошибке
      setIsCurrentCardSaved(!newSavedState);
      alert('Не удалось обновить закладку. Попробуйте еще раз.');
    } finally {
      setIsBookmarkLoading(false);
    }
  };

  // Проверяем, сохранена ли текущая карточка при её смене
  useEffect(() => {
    const checkCardSaved = async () => {
      if (activeCard?.id) {
        const saved = await isCardSaved(activeCard.id);
        setIsCurrentCardSaved(saved);
      }
    };

    checkCardSaved();
  }, [activeCard?.id]);

  // Функция для перехода к рандомной карточке
  const goToRandomCard = () => {
    if (cards.length <= 1) return; // Если карточек меньше 2, нечего рандомить

    let randomIndex;
    // Корректируем текущий индекс для сравнения: в trial вычитаем 1
    const currentCardIndex = isTrial ? activeIndex - 1 : activeIndex;

    do {
      randomIndex = Math.floor(Math.random() * cards.length);
    } while (randomIndex === currentCardIndex && cards.length > 1); // Убеждаемся, что выбрали другую карточку

    // В trial режиме добавляем смещение +1
    const targetIndex = isTrial ? randomIndex + 1 : randomIndex;

    if (showAllCards) {
      // В режиме просмотра всех карточек - просто переходим к рандомной карточке
      setActiveIndex(targetIndex);
      setShowAllCards(false);
    } else {
      // В режиме просмотра карточки - анимируем перелистывание
      if (!isNewCardAnimating) {
        setIsNewCardAnimating(true);
        setActiveIndex(targetIndex);
        setTimeout(() => {
          setIsNewCardAnimating(false);
        }, 300);
      }
    }
  };


  // Ref для кнопки поделиться
  const shareButtonRef = useRef(null);

  // Обработчик клика на кнопку поделиться
  const handleShareClick = async () => {
    try {
      // Показываем индикатор загрузки
      if (shareButtonRef.current) {
        shareButtonRef.current.innerHTML = '<div class="loading-spinner-small"></div>';
        shareButtonRef.current.disabled = true;
      }

      // Создаем картинку
      const imageDataUrl = await createShareImage(activeCard, formatText, game?.id);

      if (!imageDataUrl) {
        throw new Error("Не удалось сгенерировать изображение (пустой результат)");
      }

      // Конвертируем data URL в blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();

      // Создаем файл для поделиться
      const file = new File([blob], 'sense-card.png', { type: 'image/png' });

      // Проверяем поддержку Web Share API
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        // Используем системное окно поделиться
        await navigator.share({
          // title: 'Карточка из Sense App',
          // text: `Вопрос: ${formatText(applyGender(activeCard?.question1, getCachedGender()))}`,
          files: [file]
        });
      } else {
        // Fallback: скачиваем файл
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sense-card.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Показываем уведомление
        alert('Картинка сохранена в галерею. Теперь вы можете поделиться ею в Instagram!');
      }
    } catch (error) {
      console.error('Full share error:', error);
      if (error.name !== 'AbortError') {
        alert(`Ошибка при создании картинки: ${error.message || 'Неизвестная ошибка'}. Попробуйте еще раз.`);
      }
    } finally {
      // Восстанавливаем кнопку
      if (shareButtonRef.current) {
        shareButtonRef.current.innerHTML = '<img src="/icons/share.svg" alt="Поделиться" />';
        shareButtonRef.current.disabled = false;
      }
    }
  };

  // Удалены функции ленивой загрузки - теперь используется виртуализация

  // Функция для обновления размера контейнера виртуализации
  const updateContainerSize = () => {
    if (virtualizedContainerRef.current) {
      const rect = virtualizedContainerRef.current.getBoundingClientRect();
      console.log('updateContainerSize:', rect);
      setContainerSize({
        width: rect.width,
        height: rect.height
      });
    } else {
      console.log('updateContainerSize: ref is null');
    }
  };

  // Handle click on side areas - only for simple taps, not swipes
  const handleSideClick = (direction, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isNewCardAnimating || isDragging || hasMoved || showNoteWriting) return;
    if (direction === 'prev') {
      goPrev();
    } else if (direction === 'next') {
      goNext();
    }
  };

  // Global touch handling for swipes
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [hasMoved, setHasMoved] = useState(false); // Track if touch has moved significantly
  const cardRef = useRef(null);

  // Global touch handling for the entire overlay
  useEffect(() => {
    // Only enable touch handling in carousel mode, disable in all cards and note writing modes
    if (showAllCards || showNoteWriting) return;

    const overlay = document.querySelector('.overlay');
    if (!overlay) return;

    const threshold = 50; // Swipe threshold in pixels

    const handleTouchStart = (e) => {
      if (isNewCardAnimating) return;
      startXRef.current = e.touches[0].clientX;
      startYRef.current = e.touches[0].clientY;
      setIsDragging(true);
    };

    const handleTouchMove = (e) => {
      if (!isDragging || isNewCardAnimating) return;
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const dx = currentX - startXRef.current;
      const dy = currentY - startYRef.current;

      // Check if movement is primarily horizontal
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        setHasMoved(true);
        setDragOffset(dx);
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDragging || isNewCardAnimating) return;
      setIsDragging(false);

      const endX = e.changedTouches[0].clientX;
      const dx = endX - startXRef.current;

      // Only process swipe if there was significant horizontal movement
      if (hasMoved && Math.abs(dx) > threshold) {
        if (dx > 0) {
          goPrev();
        } else {
          goNext();
        }
      }

      setDragOffset(0);
      setHasMoved(false);
    };

    overlay.addEventListener('touchstart', handleTouchStart, { passive: true });
    overlay.addEventListener('touchmove', handleTouchMove, { passive: true });
    overlay.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      overlay.removeEventListener('touchstart', handleTouchStart);
      overlay.removeEventListener('touchmove', handleTouchMove);
      overlay.removeEventListener('touchend', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isNewCardAnimating, activeIndex, cards.length, hasMoved, showAllCards, showNoteWriting]);

  // Обработка клавиши Escape для закрытия меню всех карточек
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showAllCards) {
        e.preventDefault();
        handleAllCardsClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAllCards]);

  // Очистка blob URL при размонтировании компонента
  useEffect(() => {
    return () => {
      // Очищаем blob URL только для текущей игры при размонтировании
      // Полная очистка происходит при смене игры
      thumbCache.forEach((value, key) => {
        if (value && value.src && value.src.startsWith('blob:')) {
          URL.revokeObjectURL(value.src);
        }
      });
      thumbCache.clear();

      // Очистка завершена
    };
  }, []);

  // Плавная смена фона
  useEffect(() => {
    console.log('Background effect:', {
      activeCardUrl: activeCard?.cardPicUrl,
      currentBgUrl: bgUrl,
      isTransitioning: isBgTransitioning
    });

    if (activeCard?.cardPicUrl) {
      if (!bgUrl) {
        // Первая загрузка - устанавливаем фон сразу
        console.log('Setting initial background');
        setBgUrl(activeCard.cardPicUrl);
        // Фон загружен
      } else if (activeCard.cardPicUrl !== bgUrl) {
        // Смена карточки - плавная анимация
        console.log('Starting background transition');

        // Сначала устанавливаем новый фон как предыдущий (прозрачный)
        setPrevBgUrl(activeCard.cardPicUrl);
        setIsBgTransitioning(true);
        // Начинаем загрузку нового фона

        // Ждем загрузки нового изображения
        const newImage = new Image();
        newImage.onload = () => {
          // Фон загружен

          // Сначала устанавливаем новый фон как прозрачный
          setBgUrl(activeCard.cardPicUrl);

          // Через небольшую задержку начинаем анимацию
          setTimeout(() => {
            // Добавляем класс для анимации
            setIsBgTransitioning(true);

            // Завершаем анимацию через 2 секунды
            setTimeout(() => {
              console.log('Ending background animation');
              setIsBgTransitioning(false);
              // Убираем предыдущий фон с небольшой задержкой для плавности
              setTimeout(() => {
                setPrevBgUrl('');
              }, 100);
            }, 2000);
          }, 10);
        };
        newImage.src = activeCard.cardPicUrl;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard?.cardPicUrl, bgUrl]);

  // Обновление размера контейнера для виртуализации
  useEffect(() => {
    if (showAllCards) {
      updateContainerSize();

      const handleResize = () => {
        updateContainerSize();
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [showAllCards]);

  // Обновляем размеры при изменении containerSize
  useEffect(() => {
    if (showAllCards && containerSize.width > 0) {
      console.log('Container size updated:', containerSize);
    }
  }, [containerSize, showAllCards]);

  // Компонент для рендеринга карточки в виртуализированной сетке
  // eslint-disable-next-line no-unused-vars
  const VirtualizedCardItem = ({ columnIndex, rowIndex, style, data }) => {
    console.log('VirtualizedCardItem called:', { columnIndex, rowIndex, style, data });

    // Проверяем, что data существует
    if (!data || !data.cards || !Array.isArray(data.cards)) {
      console.log('VirtualizedCardItem: data is invalid', { data });
      return null;
    }

    const index = rowIndex * COLUMNS + columnIndex;
    const card = data.cards[index];

    if (!card) {
      console.log('VirtualizedCardItem: no card at index', { index, cardsLength: data.cards.length });
      return null;
    }

    return (
      <div style={style}>
        <div
          className={`card-item ${index === (data.activeIndex || 0) ? 'card-item--current' : ''}`}
          onClick={() => data.onCardSelect && data.onCardSelect(index)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              data.onCardSelect && data.onCardSelect(index);
            }
          }}
        >
          <div className="card-image-container">
            <img
              src={data.getOptimalImageUrl ? data.getOptimalImageUrl(card) : (card.cardThumbUrl || card.cardPicUrl)}
              alt={`Card ${index + 1}`}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="card-number">{index + 1}</div>
        </div>
      </div>
    );
  };

  const formatPrice = (priceCents) => {
    if (!priceCents || priceCents <= 0) return 'Бесплатно';
    const price = Math.round(priceCents / 100).toLocaleString('ru-RU');
    return `${price} ₽`;
  };

  const buyDigital = async () => {
    if (!game?.priceCents || game.priceCents <= 0) return;

    setBusy(true);
    try {
      const { createPayment } = await import('../../api/payments');

      // Получаем поля для формы от edge-функции
      const formData = await createPayment({
        gameId: game.id
      });

      // Создаём HTML форму
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://pro.selfwork.ru/merchant/v1/init';

      // Добавляем скрытые поля
      const fields = {
        'order_id': formData.order_id,
        'amount': formData.amount,
        'signature': formData.signature
      };

      // Добавляем info поля (товары)
      if (formData.info && Array.isArray(formData.info)) {
        formData.info.forEach((item, index) => {
          fields[`info[${index}][name]`] = item.name;
          fields[`info[${index}][quantity]`] = item.quantity;
          fields[`info[${index}][amount]`] = item.amount;
        });
      }

      // Создаём input элементы
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      // Добавляем форму в DOM и отправляем
      document.body.appendChild(form);
      form.submit();

    } catch (e) {
      console.error('buyDigital error:', e);
      alert(e.message || 'Не удалось оформить получение игры');
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`overlay overlay--gameplay ${showNoteWriting ? 'overlay--note-writing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Игра ${game?.title}`}
        style={showNoteWriting ? {
          paddingBottom: `${20 + keyboardHeight}px`
        } : {}}
      >
        {console.log('Render background:', { bgUrl, prevBgUrl, isBgTransitioning })}
        {/* Основной фон - всегда остается */}
        {bgUrl && (
          <div
            className={`overlay-bg overlay-bg--current ${isBgTransitioning ? 'bg-transitioning' : ''}`}
            aria-hidden="true"
            style={{ backgroundImage: `url(${bgUrl})` }}
            onError={(e) => console.error('Background image failed to load:', bgUrl)}
          />
        )}

        {/* Предыдущий фон для анимации */}
        {prevBgUrl && isBgTransitioning && (
          <div
            className="overlay-bg bg-transitioning"
            aria-hidden="true"
            style={{ backgroundImage: `url(${prevBgUrl})` }}
            onError={(e) => console.error('Previous background image failed to load:', prevBgUrl)}
          />
        )}

        {/* Left click area for previous card - скрывается в режиме allcards и заметки */}
        {!showAllCards && !showNoteWriting && (
          <div
            className="side-click-area side-click-area--left"
            onClick={(e) => handleSideClick('prev', e)}
          />
        )}

        {/* Right click area for next card - скрывается в режиме allcards и заметки */}
        {!showAllCards && !showNoteWriting && (
          <div
            className="side-click-area side-click-area--right"
            onClick={(e) => handleSideClick('next', e)}
          />
        )}

        {/* Upper button wrap - всегда остается, но функции кнопок меняются */}
        <div className="upper-button-wrap">
          <div className="upper-buttons">
            <button
              className="btn-40"
              aria-label={showNoteWriting ? "Закрыть заметку" : initialShowAll ? (showAllCards ? "Назад к играм" : "Назад ко всем") : (showAllCards ? "Назад к карточке" : "Закрыть")}
              onClick={showNoteWriting ? confirmCloseNoteWriting : initialShowAll ? (showAllCards ? onClose : handleProgressBadgeClick) : (showAllCards ? handleAllCardsClose : onClose)}
            >
              <OptimizedIcon
                src={showNoteWriting ? "/icons/close.svg" : "/icons/back-arrow.svg"}
                alt=""
                aria-hidden="true"
              />
            </button>
            <div className="center-actions">
              <div
                className="progress-badge"
                aria-hidden={cards.length === 0}
                onClick={showNoteWriting ? confirmCloseNoteWriting : showAllCards ? handleAllCardsClose : handleProgressBadgeClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (showNoteWriting) {
                      confirmCloseNoteWriting();
                    } else if (showAllCards) {
                      handleAllCardsClose();
                    } else {
                      handleProgressBadgeClick();
                    }
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {showNoteWriting ? (
                  <span className="note-title">Новая заметка</span>
                ) : activeCard?.isPromo ? (
                  <span className="note-title" style={{ fontSize: '14px' }}>Полный набор</span>
                ) : (
                  <>
                    <img src="/icons/all-cards.svg" alt="" className="progress-badge-icon" aria-hidden="true" />
                    <span className="current">
                      {isTrial ? activeIndex : (activeIndex % (cards.length || 1)) + 1}
                    </span>
                    <span className="sep">/</span>
                    <span className="total">{cards.length || 0}</span>
                  </>
                )}
              </div>
              {isTrial && !showNoteWriting && (
                <button
                  className="btn-40"
                  aria-label="Купить полную версию"
                  onClick={() => setShowPurchaseDialog(true)}
                >
                  <OptimizedIcon className="btn-40-icon" src="/icons/cart.svg" alt="" aria-hidden="true" />
                </button>
              )}
            </div>
            <button
              className={`btn-40 ${showNoteWriting && noteText.trim() ? 'btn-40--active' : ''}`}
              aria-label={showNoteWriting ? "Сохранить заметку" : "Случайная карточка"}
              onClick={showNoteWriting ? handleSaveNote : goToRandomCard}
              disabled={showNoteWriting && (!noteText.trim() || isNoteLoading)}
            >
              {showNoteWriting ? (
                isNoteLoading ? (
                  <div className="loading-spinner-small" />
                ) : (
                  <OptimizedIcon src="/icons/checkmark.svg" alt="" aria-hidden="true" />
                )
              ) : (
                <OptimizedIcon src="/icons/random.svg" alt="" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Основной контент - условно рендерится */}
        {!showAllCards ? (
          <>
            {showNoteWriting ? (
              /* Режим заметки */
              <div className="note-writing-content">
                {/* Карточка с вопросом */}
                <div className="note-question-card">
                  <div className="note-question-image">
                    <img
                      src={activeCard?.cardPicUrl}
                      alt="Карточка"
                      loading="eager"
                    />
                  </div>
                  <div className="note-writing-text">
                    <h3 className="note-question-title">{formatText(applyGender(activeCard?.question1, getCachedGender()))}</h3>
                    {activeCard?.question2 && (
                      <p className="note-question-subtitle">{formatText(applyGender(activeCard?.question2, getCachedGender()))}</p>
                    )}
                  </div>
                </div>

                {/* Поле для ввода заметки */}
                <div className="note-input-card">
                  <textarea
                    className="note-textarea"
                    placeholder="Все ваши заметки защищены шифрованием и доступны только вам"
                    value={noteText}
                    onChange={handleNoteTextChange}
                    maxLength={2000}
                    autoFocus
                  />
                </div>
              </div>
            ) : (
              /* Обычный режим карусели */
              <div className="overlay-content">
                <div className={`carousel-container ${isDragging ? 'dragging' : ''}`}>
                  {[-2, -1, 0, 1, 2].map((offset) => {
                    const displayCards = isTrial ? cardsWithPromo : cards;
                    const index = ((activeIndex + offset) % (displayCards.length || 1) + (displayCards.length || 1)) % (displayCards.length || 1);
                    const card = displayCards[index];
                    if (!card) return null;
                    const refProp = offset === 0 ? { ref: cardRef } : {};

                    // Calculate dynamic transforms based on drag
                    let transform = '';
                    let opacity = 1;

                    if (isDragging) {
                      const dragProgress = Math.min(Math.abs(dragOffset) / 200, 1); // 0-1 based on drag distance
                      const currentDirection = dragOffset > 0 ? 1 : -1; // Use current drag direction immediately

                      // For movement beyond 200px, continue the animation smoothly
                      const extendedProgress = Math.min(Math.abs(dragOffset) / 400, 1); // 0-1 based on 400px max
                      // For opacity changes, use 300px as the target distance
                      const opacityProgress = Math.min(Math.abs(dragOffset) / 300, 1); // 0-1 based on 300px max

                      if (offset === 0) {
                        // Active card moves with drag immediately
                        transform = `translateX(${dragOffset}px) scale(${1 - dragProgress * 0.1})`;
                        opacity = 1 - dragProgress * 0.5;
                      } else if (offset === -currentDirection) {
                        // Target card moves towards center (opposite direction of drag)
                        const moveDistance = extendedProgress * 200; // Use extended progress for movement
                        transform = `translateX(${currentDirection > 0 ? `calc(-100% - 4px + ${moveDistance}px)` : `calc(100% + 4px - ${moveDistance}px)`}) scale(${0.9 + extendedProgress * 0.1})`;
                        // Opacity reaches 1.0 at 300px and stays there
                        opacity = Math.min(0.5 + opacityProgress * 0.5, 1.0);
                      } else if (offset === currentDirection) {
                        // Opposite card moves away (same direction as drag)
                        const moveDistance = dragProgress * 100;
                        transform = `translateX(${currentDirection > 0 ? `calc(100% + 4px + ${moveDistance}px)` : `calc(-100% - 4px - ${moveDistance}px)`}) scale(${0.9 - dragProgress * 0.1})`;
                        opacity = 0.5 - dragProgress * 0.25;
                      }
                    }

                    return (
                      <div
                        key={card.id || card.cardPicUrl || index}
                        {...refProp}
                        className={`overlay-body gameplay-card carousel-card slot--${offset === -2 ? 'm2' : offset === -1 ? 'm1' : offset === 0 ? '0' : offset === 1 ? 'p1' : 'p2'}`}
                        style={{
                          transform: transform || undefined,
                          opacity: opacity !== 1 ? opacity : undefined,
                          transition: isDragging ? 'none' : 'transform 300ms ease, opacity 300ms ease'
                        }}
                      >
                        <div className="overlay-scroll">
                          <div
                            className={`game-hero-wrap gameplay-hero${game?.id === 'fb49af8b-f979-4e90-8acb-a62622aa3698' ? ' dashed-border-wrap' : ''}`}
                            style={game?.id === 'fb49af8b-f979-4e90-8acb-a62622aa3698' ? {
                              position: 'relative',
                              borderRadius: '28px'
                            } : undefined}
                          >
                            {game?.id === 'fb49af8b-f979-4e90-8acb-a62622aa3698' && (
                              <svg
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                  overflow: 'visible'
                                }}
                                preserveAspectRatio="none"
                              >
                                <rect
                                  x="4"
                                  y="4"
                                  width="100%"
                                  height="100%"
                                  rx="24"
                                  ry="24"
                                  fill="none"
                                  stroke="white"
                                  strokeWidth="8"
                                  strokeDasharray="15 15"
                                  style={{
                                    width: 'calc(100% - 8px)',
                                    height: 'calc(100% - 8px)'
                                  }}
                                />
                              </svg>
                            )}
                            <img
                              className="game-hero gameplay-hero-img"
                              src={card.cardPicUrl}
                              alt="card"
                              draggable={false}
                              loading={offset === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                            />
                          </div>
                          <div className="gameplay-content">
                            <h2 className="overlay-title gameplay-q1">{formatText(applyGender(card.question1, getCachedGender()))}</h2>
                            {card.question2 && <p className="overlay-text gameplay-q2">{formatText(applyGender(card.question2, getCachedGender()))}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabbar - скрывается в режиме allcards и заметки */}
            {!showNoteWriting && (
              <div className="tabbar-wrap">
                {activeCard?.isPromo ? (
                  // Для промо-карточки показываем кнопку покупки
                  <button
                    className="game-option-btn game-option-btn--digital"
                    style={{ marginBottom: 0 }}
                    aria-label="Купить онлайн версию"
                    onClick={() => {
                      if (game?.priceCents > 0) {
                        buyDigital();
                      } else {
                        setShowPurchaseDialog(true);
                      }
                    }}
                    disabled={busy}
                  >
                    {busy ? (
                      <div className="loading-spinner-small" style={{ margin: '0 auto', borderColor: 'white', borderRightColor: 'transparent' }} />
                    ) : (
                      <>
                        <img src="/icons/buy-digital.svg" alt="" aria-hidden="true" />
                        <div className="game-option-content">
                          <div className="game-option-text" style={{ color: 'white' }}>Купить онлайн версию</div>
                          <div className="game-option-price" style={{ color: 'white' }}>{formatPrice(game?.priceCents)} <span className="game-option-old-price">790₽</span></div>
                        </div>
                      </>
                    )}
                  </button>
                ) : (
                  // Для обычных карточек показываем стандартные кнопки
                  <>
                    <button className="tab-btn-accent-large" aria-label="Ответить" onClick={handleOpenNoteWriting}>
                      <span className="tab-btn-accent-large-icon">
                        <img src="/icons/note-white.svg" alt="" aria-hidden="true" />
                      </span>
                      Ответить
                    </button>

                    <button
                      className={`tabbar single ${isBookmarkLoading ? 'tabbar--loading' : ''}`}
                      onClick={handleBookmarkToggle}
                      disabled={isBookmarkLoading}
                      aria-label={isCurrentCardSaved ? "Удалить из закладок" : "Добавить в закладки"}
                    >
                      <img
                        src={isCurrentCardSaved ? "/icons/bookmark-active.svg" : "/icons/bookmark.svg"}
                        alt="Закладка"
                      />
                    </button>

                    <button className="tabbar single" onClick={handleShareClick} ref={shareButtonRef} aria-label="Поделиться">
                      <img src="/icons/share.svg" alt="Поделиться" />
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          /* All cards content - виртуализированная сетка */
          <div
            className="all-cards-content all-cards-content--virtualized"
            ref={virtualizedContainerRef}
            style={{
              height: '100%'
            }}
          >
            {(() => {
              // В trial режиме используем все карты, иначе только доступные
              const displayCards = isTrial && allCards.length > 0 ? allCards : cards;

              console.log('Rendering all-cards:', {
                containerSize,
                cardsLength: displayCards?.length,
                showAllCards,
                activeIndex,
                isTrial,
                trialLimit
              });

              if (!containerSize || containerSize.width <= 0 || containerSize.height <= 0) {
                return <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#666',
                  fontSize: '16px'
                }}>
                  Инициализация...
                </div>;
              }

              if (!displayCards || !Array.isArray(displayCards) || displayCards.length === 0) {
                return <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#666',
                  fontSize: '16px'
                }}>
                  Загрузка карточек...
                </div>;
              }

              const itemData = {
                cards: displayCards,
                activeIndex: activeIndex || 0,
                onCardSelect: handleCardSelect,
                getOptimalImageUrl: getOptimalImageUrl
              };

              console.log('Grid itemData:', itemData);

              // Временно используем простую сетку для отладки
              const cardDimensions = getCardDimensions();
              console.log('Card dimensions:', cardDimensions);

              // Вычисляем общую высоту контента
              const totalCards = displayCards.length;
              const rows = Math.ceil(totalCards / COLUMNS);
              const totalGapHeight = (rows - 1) * GAP;
              const contentHeight = (rows * cardDimensions.height) + totalGapHeight;

              // Добавляем отступы сверху и снизу для комфортного скролла
              const totalHeight = contentHeight + 100; // +100px для отступов

              console.log('Height calculation:', {
                totalCards,
                rows,
                cardHeight: cardDimensions.height,
                contentHeight,
                totalHeight
              });

              return (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${COLUMNS}, ${cardDimensions.width}px)`,
                  gap: `${GAP}px`,
                  justifyContent: 'center',
                  paddingBottom: '120px' // дополнительный отступ снизу для скролла
                }}>
                  {displayCards.map((card, index) => {
                    const isLocked = isTrial && trialLimit > 0 && index >= trialLimit;
                    return (
                      <div
                        key={card.id || index}
                        className={`card-item ${index === activeIndex ? 'card-item--current' : ''} ${isLocked ? 'card-item--locked' : ''}`}
                        onClick={() => handleCardSelect(index)}
                        role="button"
                        tabIndex={0}
                        style={{
                          width: cardDimensions.width,
                          height: cardDimensions.height
                        }}
                      >
                        <div className="card-image-container">
                          <img
                            className="card-image"
                            src={getOptimalImageUrl(card)}
                            alt={`Card ${index + 1}`}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        {!savedCardsMode && <div className="card-number">{index + 1}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Диалог подтверждения удаления черновика */}
      {showConfirmDialog && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <div className="confirm-dialog-content">
              <h3 className="confirm-dialog-title">Удалить черновик заметки?</h3>
              <p className="confirm-dialog-message">Ваш текст будет потерян</p>
            </div>
            <div className="confirm-dialog-buttons">
              <button
                className="confirm-dialog-btn confirm-dialog-btn--cancel"
                onClick={handleCancelDelete}
              >
                Вернуться
              </button>
              <button
                className="confirm-dialog-btn confirm-dialog-btn--delete"
                onClick={handleConfirmDelete}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Диалог предложения покупки */}
      {showPurchaseDialog && (
        <div
          className="purchase-dialog-overlay"
          onClick={() => setShowPurchaseDialog(false)}
        >
          <div
            className="purchase-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="purchase-dialog-title">Полный набор</h3>
            <p className="purchase-dialog-message">Купите полную версию и откройте все карточки</p>

            <button
              className="purchase-dialog-btn purchase-dialog-btn--buy"
              onClick={() => {
                setShowPurchaseDialog(false);
                buyDigital();
              }}
              disabled={busy}
            >
              {busy ? (
                <div className="loading-spinner-small" />
              ) : (
                <>
                  <img src="/icons/buy-digital.svg" alt="" />
                  <div className="purchase-dialog-btn-content">
                    <div className="purchase-dialog-btn-text">Купить онлайн версию</div>
                    <div className="purchase-dialog-btn-price">{formatPrice(game?.priceCents)} <span className="game-option-old-price">790₽</span></div>
                  </div>
                </>
              )}
            </button>

            <button
              className="purchase-dialog-btn purchase-dialog-btn--later"
              onClick={() => setShowPurchaseDialog(false)}
            >
              Может быть позже
            </button>
          </div>
        </div>
      )}
    </>
  );
}
